import { Box, Paper, Typography } from '@shared/ui';
import Icon from '@shared/ui/components/Icon';
import type { FC } from 'react';

import GradientActionButton from './GradientActionButton';

type ReflectionSelectionMode = 'chem-call' | 'coaching';

export interface SelectionActionBarProps {
  /** Number of documents currently selected */
  selectedCount: number;
  /** Singular label for the selected item type */
  selectedItemLabel?: string;
  /** Optional status text shown under the selection count */
  statusLabel?: string | null;
  /** Handler for the generate summary action */
  onGenerateSummary: () => void;
  /** Handler for the generate coaching reflection action */
  onGenerateFeedback: () => void;
  /** Handler for the generate session prep action */
  onGenerateSessionPrep: () => void;
  /** Handler for the generate milestones and wins action */
  onGenerateMilestonesAndWins: () => void;
  /** Whether the button should be disabled */
  disabled?: boolean;
  /** Whether only the feedback action should be disabled */
  feedbackDisabled?: boolean;
  /** Optional tooltip override for disabled feedback action */
  feedbackDisabledReason?: string | null;
  /** Whether only the summary action should be disabled */
  summaryDisabled?: boolean;
  /** Optional tooltip override for disabled summary action */
  summaryDisabledReason?: string | null;
  /** Whether to show the coaching/chem-call feedback action */
  showFeedbackAction?: boolean;
  /** Whether to show the summary action */
  showSummaryAction?: boolean;
  /** Optional label override for the feedback action */
  feedbackLabel?: string;
  /** Semantic mode for feedback copy */
  feedbackMode?: ReflectionSelectionMode;
}

/**
 * SelectionActionBar displays at the bottom when documents are selected.
 * Shows selected count and available recipe actions for the current selection size.
 */
const SelectionActionBar: FC<SelectionActionBarProps> = ({
  selectedCount,
  selectedItemLabel = 'Document',
  statusLabel = null,
  onGenerateSummary,
  onGenerateFeedback,
  onGenerateSessionPrep,
  onGenerateMilestonesAndWins,
  disabled = false,
  feedbackDisabled = false,
  feedbackDisabledReason = null,
  showFeedbackAction = true,
  showSummaryAction = true,
  summaryDisabled = false,
  summaryDisabledReason = null,
  feedbackLabel = 'Coaching Reflection',
  feedbackMode = 'coaching',
}) => {
  if (selectedCount === 0) {
    return null;
  }

  // Temporarily hidden for production release; re-enable when these recipes are ready.
  const enableSessionPrepRecipe = false;
  const enableMilestonesAndWinsRecipe = false;

  const showCoachingReflection = showFeedbackAction && selectedCount >= 1;
  const showSessionPrep = enableSessionPrepRecipe && selectedCount === 1;
  const showMilestonesAndWins = enableMilestonesAndWinsRecipe && selectedCount >= 2;
  const summaryTooltip =
    selectedCount > 1
      ? 'Coaching Arc - See patterns, insights, and growth across multiple sessions'
      : 'Session Summary - Summarize a single coaching session with action items, quotes, and insights';
  const feedbackTooltip =
    feedbackMode === 'chem-call'
      ? 'Chem Call Reflection (single and multiple sessions) - Assess chem call performance for growth opportunities and practice areas'
      : 'Coaching Reflection (single and multiple sessions) - Assess your performance for growth opportunities and practice areas';

  return (
    <Paper
      elevation={6}
      sx={{
        position: 'sticky',
        bottom: 16,
        mt: 3,
        p: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: 'white',
        borderRadius: '16px',
        border: '1px solid',
        borderColor: 'poppy.500',
        boxShadow:
          '0px 1px 18px 0px rgba(0, 0, 0, 0.1), 0px 6px 10px 0px rgba(0, 0, 0, 0.1), 0px 3px 5px -1px rgba(0, 0, 0, 0.1)',
      }}
    >
      <Box>
        <Typography
          sx={{
            fontFamily: 'Basis Grotesque',
            fontWeight: 700,
            fontSize: '16px',
            lineHeight: 1.5,
            letterSpacing: '0.15px',
            color: 'cement.500',
          }}
        >
          {selectedCount} {selectedItemLabel}
          {selectedCount !== 1 ? 's' : ''} Selected.
        </Typography>
        {statusLabel ? (
          <Typography
            sx={{
              fontFamily: 'Basis Grotesque',
              fontWeight: 500,
              fontSize: '13px',
              lineHeight: 1.4,
              color: 'cement.400',
              mt: 0.25,
            }}
          >
            {statusLabel}
          </Typography>
        ) : null}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        {showCoachingReflection && (
          <GradientActionButton
            onClick={onGenerateFeedback}
            disabled={disabled || feedbackDisabled}
            startIcon={<Icon>auto_awesome</Icon>}
            tooltip={feedbackDisabledReason || feedbackTooltip}
          >
            {feedbackLabel}
          </GradientActionButton>
        )}
        {showSessionPrep && (
          <GradientActionButton
            onClick={onGenerateSessionPrep}
            disabled={disabled}
            startIcon={<Icon>auto_awesome</Icon>}
            tooltip="Prep for Next Session - Get a high-level overview of last session’s topics and next steps"
          >
            Session Prep
          </GradientActionButton>
        )}
        {showMilestonesAndWins && (
          <GradientActionButton
            onClick={onGenerateMilestonesAndWins}
            disabled={disabled}
            startIcon={<Icon>auto_awesome</Icon>}
          >
            Milestones and Wins
          </GradientActionButton>
        )}
        {showSummaryAction && (
          <GradientActionButton
            onClick={onGenerateSummary}
            disabled={disabled || summaryDisabled}
            startIcon={<Icon>auto_awesome</Icon>}
            tooltip={summaryDisabledReason || summaryTooltip}
          >
            {selectedCount > 1 ? 'Coaching Arc Summary' : 'Session Summary'}
          </GradientActionButton>
        )}
      </Box>
    </Paper>
  );
};

export default SelectionActionBar;
